import { Prisma } from "@prisma/client";

export interface PageSpeedResult {
  performanceScore?: number;
  accessibilityScore?: number;
  bestPracticesScore?: number;
  seoScore?: number;
}

// API Request: URL + Strategy → Raw Response
export async function getPageSpeedData(
  url: string,
  strategy: "mobile" | "desktop" = "mobile"
): Promise<unknown> {
  const apiKey = process.env.GOOGLE_API_KEY;
  if (!apiKey) throw new Error("GOOGLE_API_KEY is not set");

  const apiUrl = `https://www.googleapis.com/pagespeedonline/v5/runPagespeed?url=${encodeURIComponent(
    url
  )}&strategy=${strategy}&category=PERFORMANCE&category=ACCESSIBILITY&category=BEST_PRACTICES&category=SEO&key=${apiKey}`;

  const response = await fetch(apiUrl);
  if (!response.ok) {
    let errorDetails = "";
    try {
      const errorData = await response.json();
      errorDetails = errorData.error?.message || errorData.message || "";
    } catch {}
    const message = `PageSpeed API error for URL "${url}" (strategy ${strategy}): ${
      response.status
    } ${response.statusText}${errorDetails ? ` - ${errorDetails}` : ""}`;
    console.error(message);
    throw new Error(message);
  }

  return await response.json();
}

// Mapping: Raw Response → Scores
export function mapPageSpeedToScores(raw: unknown): PageSpeedResult {
  const categories = (
    raw as {
      lighthouseResult?: { categories?: Record<string, { score?: number }> };
    }
  )?.lighthouseResult?.categories;

  if (!categories) return {};

  return {
    performanceScore:
      typeof categories["performance"]?.score === "number"
        ? Math.round((categories["performance"].score as number) * 100)
        : undefined,
    accessibilityScore:
      typeof categories["accessibility"]?.score === "number"
        ? Math.round((categories["accessibility"].score as number) * 100)
        : undefined,
    bestPracticesScore:
      typeof categories["best-practices"]?.score === "number"
        ? Math.round((categories["best-practices"].score as number) * 100)
        : undefined,
    seoScore:
      typeof categories["seo"]?.score === "number"
        ? Math.round((categories["seo"].score as number) * 100)
        : undefined,
  };
}

// DB Mapping: PageSpeed Data → DB Format
export function mapPageSpeedToDb(
  scores: PageSpeedResult,
  summary: unknown,
  url: string,
  strategy: "mobile" | "desktop",
  reportId: string
) {
  return {
    url,
    strategy,
    performanceScore: scores.performanceScore ?? null,
    accessibilityScore: scores.accessibilityScore ?? null,
    bestPracticesScore: scores.bestPracticesScore ?? null,
    seoScore: scores.seoScore ?? null,
    summary: summary as Prisma.InputJsonValue,
    reportId,
  };
}

// Overall Score Calculation
export function calculatePsiOverallScore(
  mobile?: PageSpeedResult,
  desktop?: PageSpeedResult
): number | null {
  const scores: number[] = [];

  if (mobile) {
    if (mobile.performanceScore !== undefined)
      scores.push(mobile.performanceScore);
    if (mobile.accessibilityScore !== undefined)
      scores.push(mobile.accessibilityScore);
    if (mobile.bestPracticesScore !== undefined)
      scores.push(mobile.bestPracticesScore);
    if (mobile.seoScore !== undefined) scores.push(mobile.seoScore);
  }

  if (desktop) {
    if (desktop.performanceScore !== undefined)
      scores.push(desktop.performanceScore);
    if (desktop.accessibilityScore !== undefined)
      scores.push(desktop.accessibilityScore);
    if (desktop.bestPracticesScore !== undefined)
      scores.push(desktop.bestPracticesScore);
    if (desktop.seoScore !== undefined) scores.push(desktop.seoScore);
  }

  if (scores.length === 0) return null;

  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

export function formatMetricValue(
  value: number | undefined,
  unit: string = "ms"
): string {
  if (value === undefined) return "N/A";
  if (unit === "score") return `${value}/100`;
  if (value < 1000) return `${Math.round(value)}${unit}`;
  return `${(value / 1000).toFixed(1)}s`;
}
